import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Plus,
  Search,
  MoreHorizontal,
  Pencil,
  Trash2,
  Building2,
  Loader2,
  Store,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

export const COMPANY_TYPES = [
  { value: "employer", label: "Munkáltató", short: "munkáltató" },
  { value: "franchise", label: "Franchise partner", short: "franchise" },
];

export const COMPANY_STATUS = [
  { value: "active", label: "Aktív", tone: "emerald" },
  { value: "inactive", label: "Inaktív", tone: "muted" },
];

const STATUS_TONES = {
  emerald:
    "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-400",
  muted: "border-border bg-muted text-muted-foreground",
};

const STATUS_DOT_TONES = {
  emerald: "bg-emerald-500",
  muted: "bg-muted-foreground/60",
};

const emptyForm = {
  name: "",
  type: "employer",
  tax_number: "",
  registration_number: "",
  address: "",
  email: "",
  phone: "",
  contact_person: "",
  notes: "",
  status: "active",
};

export function CompanyTypeBadge({ type }) {
  const meta = COMPANY_TYPES.find((t) => t.value === type) || COMPANY_TYPES[0];
  const isFranchise = type === "franchise";
  return (
    <span
      data-testid={`company-type-${type}`}
      className={cn(
        "inline-flex h-5 items-center gap-1.5 rounded-md border px-2 text-[11px] font-medium",
        isFranchise
          ? "border-violet-200 bg-violet-50 text-violet-700 dark:border-violet-500/20 dark:bg-violet-500/10 dark:text-violet-400"
          : "border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-500/20 dark:bg-blue-500/10 dark:text-blue-400"
      )}
    >
      {isFranchise ? (
        <Store className="h-3 w-3" strokeWidth={1.75} />
      ) : (
        <Building2 className="h-3 w-3" strokeWidth={1.75} />
      )}
      {meta.label}
    </span>
  );
}

export function CompanyStatusBadge({ value }) {
  const meta = COMPANY_STATUS.find((s) => s.value === value) || COMPANY_STATUS[0];
  return (
    <span
      data-testid={`company-status-${value}`}
      className={cn(
        "inline-flex h-5 items-center gap-1.5 rounded-full border px-2 text-[11px] font-medium",
        STATUS_TONES[meta.tone]
      )}
    >
      <span className={cn("h-1.5 w-1.5 rounded-full", STATUS_DOT_TONES[meta.tone])} />
      {meta.label}
    </span>
  );
}

export function CompanyFormDialog({ open, onOpenChange, company, onSaved }) {
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    if (company) {
      setForm({
        name: company.name || "",
        type: company.type || "employer",
        tax_number: company.tax_number || "",
        registration_number: company.registration_number || "",
        address: company.address || "",
        email: company.email || "",
        phone: company.phone || "",
        contact_person: company.contact_person || "",
        notes: company.notes || "",
        status: company.status || "active",
      });
    } else {
      setForm(emptyForm);
    }
    setError("");
  }, [open, company]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      const payload = Object.fromEntries(
        Object.entries(form).map(([k, v]) => [k, v === "" ? null : v])
      );
      payload.name = form.name;
      payload.type = form.type;
      payload.status = form.status;
      let result;
      if (company) {
        const { data } = await api.patch(`/companies/${company._id}`, payload);
        result = data;
        toast.success("Cég frissítve.");
      } else {
        const { data } = await api.post("/companies", payload);
        result = data;
        toast.success("Cég létrehozva.");
      }
      onOpenChange(false);
      if (onSaved) onSaved(result);
    } catch (err) {
      setError(
        formatApiError(err?.response?.data?.detail) || "Mentés sikertelen."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="company-form-dialog" className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{company ? "Cég szerkesztése" : "Új cég"}</DialogTitle>
          <DialogDescription>
            {company
              ? "Frissítsd a cég adatait. A változások azonnal mentésre kerülnek."
              : "Munkáltató cég vagy franchise partner felvétele a rendszerbe."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          <Field label="Cégnév *" testId="form-name">
            <Input
              required
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="sipx Központ Kft."
            />
          </Field>
          <Grid cols={2}>
            <Field label="Típus" testId="form-type">
              <Select
                value={form.type}
                onValueChange={(v) => setForm((f) => ({ ...f, type: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {COMPANY_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Állapot" testId="form-status">
              <Select
                value={form.status}
                onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {COMPANY_STATUS.map((s) => (
                    <SelectItem key={s.value} value={s.value}>
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </Grid>
          <Grid cols={2}>
            <Field label="Adószám" testId="form-tax-number">
              <Input
                value={form.tax_number}
                onChange={(e) =>
                  setForm((f) => ({ ...f, tax_number: e.target.value }))
                }
                placeholder="12345678-2-42"
                className="font-mono"
              />
            </Field>
            <Field label="Cégjegyzékszám" testId="form-registration-number">
              <Input
                value={form.registration_number}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    registration_number: e.target.value,
                  }))
                }
                placeholder="01-09-123456"
                className="font-mono"
              />
            </Field>
          </Grid>
          <Field label="Cím" testId="form-address">
            <Textarea
              value={form.address}
              onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
              placeholder="1234 Budapest, Példa utca 1."
              rows={2}
            />
          </Field>
          <Field label="Kapcsolattartó" testId="form-contact-person">
            <Input
              value={form.contact_person}
              onChange={(e) =>
                setForm((f) => ({ ...f, contact_person: e.target.value }))
              }
              placeholder="Nagy Bence"
            />
          </Field>
          <Grid cols={2}>
            <Field label="E-mail" testId="form-email">
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                placeholder="info@sipxgroup.hu"
              />
            </Field>
            <Field label="Telefon" testId="form-phone">
              <Input
                value={form.phone}
                onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                placeholder="+36 1 234 5678"
              />
            </Field>
          </Grid>
          <Field label="Megjegyzés" testId="form-notes">
            <Textarea
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              placeholder="Belső megjegyzések, szerződéses adatok…"
              rows={2}
            />
          </Field>
          {error && (
            <div
              data-testid="form-error"
              className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive"
            >
              {error}
            </div>
          )}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              data-testid="cancel-company-form"
            >
              Mégse
            </Button>
            <Button type="submit" disabled={saving} data-testid="submit-company-form">
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Mentés…
                </>
              ) : company ? (
                "Mentés"
              ) : (
                "Létrehozás"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function CompaniesPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/companies");
      setItems(data);
    } catch (e) {
      toast.error(
        formatApiError(e?.response?.data?.detail) || "Nem sikerült betölteni a cégeket."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items
      .filter((c) => (typeFilter === "all" ? true : c.type === typeFilter))
      .filter((c) => {
        if (!q) return true;
        const hay = [c.name, c.tax_number, c.registration_number, c.contact_person, c.email]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return hay.includes(q);
      });
  }, [items, query, typeFilter]);

  const stats = useMemo(() => {
    const total = items.length;
    const employer = items.filter((c) => c.type === "employer").length;
    const franchise = items.filter((c) => c.type === "franchise").length;
    const sites = items.reduce((acc, c) => acc + (c.sites_count || 0), 0);
    const employees = items.reduce((acc, c) => acc + (c.employees_count || 0), 0);
    return { total, employer, franchise, sites, employees };
  }, [items]);

  const upsertItem = (saved) => {
    if (!saved) return;
    setItems((prev) => {
      const idx = prev.findIndex((c) => c._id === saved._id);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], ...saved };
        return next;
      }
      return [saved, ...prev];
    });
  };

  const onDelete = async (company) => {
    if (!window.confirm(`Biztosan törlöd "${company.name}" céget?`)) return;
    try {
      await api.delete(`/companies/${company._id}`);
      toast.success("Cég törölve.");
      fetchAll();
    } catch (err) {
      toast.error(
        formatApiError(err?.response?.data?.detail) || "Nem sikerült törölni."
      );
    }
  };

  return (
    <div data-testid="companies-page" className="space-y-8">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
            Vállalati struktúra
          </p>
          <h1 className="mt-1 text-2xl font-semibold tracking-tight sm:text-3xl">
            Cégek
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Munkáltató cégek és franchise partnerek nyilvántartása.
          </p>
        </div>
        <Button
          data-testid="new-company-button"
          onClick={() => {
            setEditing(null);
            setOpen(true);
          }}
          className="h-9 self-start sm:self-auto"
        >
          <Plus className="h-4 w-4" />
          Új cég
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        <StatCard label="Összes cég" value={stats.total} testId="stat-total" />
        <StatCard
          label="Munkáltató"
          value={stats.employer}
          testId="stat-employer"
          dot="bg-blue-500"
        />
        <StatCard
          label="Franchise"
          value={stats.franchise}
          testId="stat-franchise"
          dot="bg-violet-500"
        />
        <StatCard label="Telephelyek" value={stats.sites} testId="stat-sites" />
        <StatCard label="Munkavállalók" value={stats.employees} testId="stat-employees" />
      </div>

      <div className="overflow-hidden rounded-xl border border-border bg-[hsl(var(--sipx-elevated))]">
        <div className="flex flex-wrap items-center gap-3 border-b border-border px-4 py-3">
          <div className="flex flex-1 items-center gap-2">
            <Search className="h-4 w-4 text-muted-foreground" strokeWidth={1.75} />
            <input
              data-testid="companies-search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Keresés cégnév, adószám, kapcsolattartó alapján…"
              className="h-8 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            />
          </div>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger
              data-testid="companies-type-filter"
              className="h-8 w-[200px]"
            >
              <SelectValue placeholder="Típus" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Minden típus</SelectItem>
              {COMPANY_TYPES.map((t) => (
                <SelectItem key={t.value} value={t.value}>
                  {t.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Mobile card list */}
        <div className="space-y-2 px-3 py-3 md:hidden" data-testid="companies-mobile-list">
          {loading ? (
            <p className="px-1 py-8 text-center text-sm text-muted-foreground">Betöltés…</p>
          ) : filtered.length === 0 ? (
            <EmptyState
              onAction={() => { setEditing(null); setOpen(true); }}
              hasQuery={query.length > 0 || typeFilter !== "all"}
            />
          ) : (
            filtered.map((c) => (
              <button
                key={c._id}
                type="button"
                data-testid={`company-card-${c._id}`}
                onClick={() => navigate(`/companies/${c._id}`)}
                className="flex w-full items-center gap-3 rounded-2xl border border-border bg-card px-3 py-3 text-left transition-colors active:bg-foreground/[0.04]"
              >
                <span
                  className={cn(
                    "grid h-10 w-10 shrink-0 place-items-center rounded-xl text-background",
                    c.type === "franchise" ? "bg-violet-600" : "bg-blue-600"
                  )}
                >
                  {c.type === "franchise" ? (
                    <Store className="h-4 w-4" strokeWidth={1.75} />
                  ) : (
                    <Building2 className="h-4 w-4" strokeWidth={1.75} />
                  )}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[13.5px] font-medium">{c.name}</p>
                  <p className="truncate text-[11px] text-muted-foreground">
                    {c.contact_person || c.tax_number || "—"}
                  </p>
                  <div className="mt-1 flex items-center gap-2 text-[10.5px] text-muted-foreground">
                    <span className="font-mono tabular-nums">
                      {c.sites_count || 0} telephely
                    </span>
                    <span className="text-slate-300">·</span>
                    <span className="font-mono tabular-nums">
                      {c.employees_count || 0} dolgozó
                    </span>
                  </div>
                </div>
                <CompanyTypeBadge type={c.type} />
              </button>
            ))
          )}
        </div>

        <div className="hidden overflow-x-auto md:block">
          <table className="w-full text-sm" data-testid="companies-table">
            <thead>
              <tr className="border-b border-border bg-muted/30 text-muted-foreground">
                <Th>Cégnév</Th>
                <Th>Típus</Th>
                <Th className="hidden md:table-cell">Telephely</Th>
                <Th className="hidden md:table-cell">Munkavállaló</Th>
                <Th className="hidden lg:table-cell">Adószám</Th>
                <Th className="hidden sm:table-cell">Állapot</Th>
                <Th className="pr-4 text-right">Műveletek</Th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <SkeletonRows />
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={7}>
                    <EmptyState
                      onAction={() => {
                        setEditing(null);
                        setOpen(true);
                      }}
                      hasQuery={query.length > 0 || typeFilter !== "all"}
                    />
                  </td>
                </tr>
              ) : (
                filtered.map((c) => (
                  <tr
                    key={c._id}
                    data-testid={`company-row-${c._id}`}
                    onClick={() => navigate(`/companies/${c._id}`)}
                    className="cursor-pointer border-b border-border last:border-0 transition-colors hover:bg-foreground/[0.025]"
                  >
                    <Td>
                      <div className="flex items-center gap-3">
                        <span
                          className={cn(
                            "grid h-8 w-8 place-items-center rounded-md text-background",
                            c.type === "franchise" ? "bg-violet-600" : "bg-blue-600"
                          )}
                        >
                          {c.type === "franchise" ? (
                            <Store className="h-4 w-4" strokeWidth={1.75} />
                          ) : (
                            <Building2 className="h-4 w-4" strokeWidth={1.75} />
                          )}
                        </span>
                        <div className="flex flex-col leading-tight">
                          <span className="font-medium">{c.name}</span>
                          <span className="text-[11px] text-muted-foreground">
                            {c.contact_person || "—"}
                          </span>
                        </div>
                      </div>
                    </Td>
                    <Td>
                      <CompanyTypeBadge type={c.type} />
                    </Td>
                    <Td className="hidden font-mono text-[12.5px] text-muted-foreground md:table-cell">
                      {c.sites_count || 0}
                    </Td>
                    <Td className="hidden font-mono text-[12.5px] text-muted-foreground md:table-cell">
                      {c.employees_count || 0}
                    </Td>
                    <Td className="hidden font-mono text-[12px] text-muted-foreground lg:table-cell">
                      {c.tax_number || "—"}
                    </Td>
                    <Td className="hidden sm:table-cell">
                      <CompanyStatusBadge value={c.status} />
                    </Td>
                    <Td
                      className="pr-2 text-right"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            data-testid={`row-actions-${c._id}`}
                            aria-label="Sor műveletek"
                            className="grid h-8 w-8 place-items-center rounded-md text-muted-foreground hover:bg-foreground/[0.06] hover:text-foreground"
                          >
                            <MoreHorizontal className="h-4 w-4" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-44">
                          <DropdownMenuLabel>Műveletek</DropdownMenuLabel>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            data-testid={`edit-company-${c._id}`}
                            onClick={() => {
                              setEditing(c);
                              setOpen(true);
                            }}
                          >
                            <Pencil className="h-4 w-4" />
                            Szerkesztés
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            data-testid={`delete-company-${c._id}`}
                            onClick={() => onDelete(c)}
                            className="text-destructive focus:text-destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                            Törlés
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </Td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <CompanyFormDialog
        open={open}
        onOpenChange={setOpen}
        company={editing}
        onSaved={(saved) => {
          upsertItem(saved);
          fetchAll();
        }}
      />
    </div>
  );
}

function Th({ children, className = "" }) {
  return (
    <th
      className={cn(
        "h-10 whitespace-nowrap px-4 text-left align-middle text-[11px] font-medium uppercase tracking-[0.12em]",
        className
      )}
    >
      {children}
    </th>
  );
}

function Td({ children, className = "", ...rest }) {
  return (
    <td
      className={cn("h-16 whitespace-nowrap px-4 align-middle", className)}
      {...rest}
    >
      {children}
    </td>
  );
}

function StatCard({ label, value, testId, dot = "bg-foreground/30" }) {
  return (
    <div
      data-testid={testId}
      className="rounded-xl border border-border bg-[hsl(var(--sipx-elevated))] p-5"
    >
      <p className="inline-flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
        <span className={cn("h-1.5 w-1.5 rounded-full", dot)} />
        {label}
      </p>
      <p className="mt-2 font-mono text-2xl font-semibold tracking-tight">{value}</p>
    </div>
  );
}

function Field({ label, children, testId }) {
  return (
    <label className="block space-y-1.5" data-testid={testId}>
      <span className="block text-xs font-medium text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}

function Grid({ cols = 2, children }) {
  return (
    <div
      className={cn(
        "grid gap-3",
        cols === 2 && "grid-cols-1 sm:grid-cols-2",
        cols === 3 && "grid-cols-1 sm:grid-cols-3"
      )}
    >
      {children}
    </div>
  );
}

function SkeletonRows() {
  return (
    <>
      {[0, 1, 2].map((i) => (
        <tr key={i} className="border-b border-border last:border-0">
          {[0, 1, 2, 3, 4, 5, 6].map((j) => (
            <td key={j} className="h-16 px-4">
              <div className="h-3 w-3/4 animate-pulse rounded bg-muted" />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}

function EmptyState({ onAction, hasQuery }) {
  return (
    <div
      data-testid="companies-empty-state"
      className="flex flex-col items-center justify-center gap-3 px-6 py-16 text-center"
    >
      <div className="grid h-10 w-10 place-items-center rounded-full border border-border bg-background">
        <Building2 className="h-4 w-4 text-muted-foreground" strokeWidth={1.75} />
      </div>
      <div className="space-y-1">
        <p className="text-sm font-medium">
          {hasQuery ? "Nincs találat" : "Nincsenek cégek"}
        </p>
        <p className="text-xs text-muted-foreground">
          {hasQuery
            ? "Próbálj meg másik keresést, vagy állítsd vissza a szűrőt."
            : "Adj hozzá egy munkáltató céget vagy franchise partnert."}
        </p>
      </div>
      {!hasQuery && (
        <Button onClick={onAction} variant="outline" className="mt-2 h-8">
          <Plus className="h-3.5 w-3.5" />
          Új cég hozzáadása
        </Button>
      )}
    </div>
  );
}
